import HttpClient, { HttpClientError } from '@baarbaanet/http-client'
import {
	HttpClientMethod,
	HttpClientResponse
} from '@baarbaanet/http-client/dist/types'

import { getConfig, setCredentials } from './config'
import {
	createUrl,
	generateConfigs,
	transformXhrResponseHeaders
} from './helpers'
import {
	ApiServiceCredentials,
	ApiServiceRequestOptions,
	ApiServiceRequestOptionsWithData,
	ApiServiceResponse,
	ApiServiceUploadOptions,
	ContentUploadResponse
} from './types'

let refreshTokenPromise: Nullable<Promise<ApiServiceCredentials>> = null
function refreshToken() {
	const {
		refreshTokenApi,
		revokeTokenApi,
		credentials,
		onRefreshToken,
		onRevokeToken
	} = getConfig()

	if (!credentials || !refreshTokenApi || !revokeTokenApi) {
		setCredentials(null)
		onRevokeToken?.()
		return Promise.reject(new Error('No refresh token available'))
	}

	if (refreshTokenPromise) {
		return refreshTokenPromise
	}

	refreshTokenPromise = (async () => {
		try {
			const response = await request<never, ApiServiceCredentials>(
				refreshTokenApi.method,
				refreshTokenApi.scope,
				refreshTokenApi.path,
				{
					version: refreshTokenApi.version,
					headers: {
						Authorization: `Bearer ${credentials.refreshToken}`
					}
				}
			)

			if (!response?.data) {
				setCredentials(null)
				onRevokeToken?.()
				throw new Error('[ApiService] refresh token response is empty')
			}

			const newCredentials = {
				accessToken: response.data.content.accessToken,
				refreshToken: response.data.content.refreshToken
			}

			setCredentials(newCredentials)
			onRefreshToken?.(newCredentials)

			return newCredentials
		} catch (err) {
			setCredentials(null)
			onRevokeToken?.()
			throw err
		} finally {
			refreshTokenPromise = null
		}
	})()

	return refreshTokenPromise
}

async function request<Data = unknown, Response = unknown>(
	method: HttpClientMethod,
	scope: string,
	path: string,
	options:
		| ApiServiceRequestOptions
		| ApiServiceRequestOptionsWithData<Data> = {}
) {
	try {
		const response = await HttpClient<Data, ApiServiceResponse<Response>>(
			createUrl(scope, path, options.version),
			generateConfigs(method, options)
		)

		return Promise.resolve(response)
	} catch (e) {
		if (e instanceof Error && e.name === 'HttpClientError') {
			const error = e as HttpClientError

			const { revokeTokenApi, onRevokeToken, refreshTokenApi, credentials } =
				getConfig()

			const requestScope = error.url?.replace('://', '').split('/')[1]

			if (
				error.response &&
				error.response.status === 401 &&
				credentials &&
				refreshTokenApi &&
				revokeTokenApi
			) {
				if (
					(revokeTokenApi.scope === requestScope &&
						error.url?.includes(revokeTokenApi.path)) ||
					(refreshTokenApi.scope === requestScope &&
						error.url?.includes(refreshTokenApi.path))
				) {
					setCredentials(null)
					onRevokeToken?.()
					return Promise.reject(e)
				} else {
					try {
						await refreshToken()

						return request(method, scope, path, options)
					} catch (refreshTokenError) {
						return Promise.reject(refreshTokenError)
					}
				}
			}
		}

		return Promise.reject(e)
	}
}

export async function raw<Data = unknown, Response = unknown>(
	method: HttpClientMethod,
	url: string,
	options:
		| ApiServiceRequestOptions
		| ApiServiceRequestOptionsWithData<Data> = {}
) {
	try {
		const response = await HttpClient<Data, ApiServiceResponse<Response>>(
			url,
			generateConfigs(method, options)
		)

		return Promise.resolve(response)
	} catch (e) {
		if (e instanceof Error && e.name === 'HttpClientError') {
			const error = e as HttpClientError

			const { revokeTokenApi, onRevokeToken, refreshTokenApi, credentials } =
				getConfig()

			const requestScope = error.url?.replace('://', '').split('/')[1]

			if (
				error.response &&
				error.response.status === 401 &&
				credentials &&
				refreshTokenApi &&
				revokeTokenApi
			) {
				if (
					(revokeTokenApi.scope === requestScope &&
						error.url?.includes(revokeTokenApi.path)) ||
					(refreshTokenApi.scope === requestScope &&
						error.url?.includes(refreshTokenApi.path))
				) {
					setCredentials(null)
					onRevokeToken?.()
					return Promise.reject(e)
				} else {
					try {
						await refreshToken()

						return raw(method, url, options)
					} catch (refreshTokenError) {
						return Promise.reject(refreshTokenError)
					}
				}
			}
		}

		return Promise.reject(e)
	}
}

export async function get<Response = unknown>(
	scope: string,
	path: string,
	options?: ApiServiceRequestOptions
) {
	return await request<unknown, Response>('GET', scope, path, options)
}

export async function post<Data = unknown, Response = unknown>(
	scope: string,
	path: string,
	options?: ApiServiceRequestOptionsWithData<Data>
) {
	return await request<Data, Response>('POST', scope, path, options)
}

export async function patch<Data = unknown, Response = unknown>(
	scope: string,
	path: string,
	options?: ApiServiceRequestOptionsWithData<Data>
) {
	return await request<Data, Response>('PATCH', scope, path, options)
}

export async function put<Data = unknown, Response = unknown>(
	scope: string,
	path: string,
	options?: ApiServiceRequestOptionsWithData<Data>
) {
	return await request<Data, Response>('PUT', scope, path, options)
}

export async function delete_<Data = unknown, Response = unknown>(
	scope: string,
	path: string,
	options?: ApiServiceRequestOptionsWithData<Data>
) {
	return await request<Data, Response>('DELETE', scope, path, options)
}

export async function download(
	id: string,
	options: Omit<ApiServiceRequestOptions, 'version'> = {}
) {
	const { downloadApi } = getConfig()
	const opts = {
		...options,
		params: {
			...(options.params || {}),
			id
		}
	} as Omit<ApiServiceRequestOptions, 'version'>

	return await HttpClient<unknown, Blob>(
		createUrl(downloadApi.scope, downloadApi.path, downloadApi.version),
		generateConfigs(downloadApi.method, opts)
	)
}

export async function upload(
	files: File[],
	type: number,
	options: ApiServiceUploadOptions = {}
): Promise<
	HttpClientResponse<unknown, ApiServiceResponse<ContentUploadResponse> | null>
> {
	return new Promise((resolve, reject) => {
		const { uploadApi, revokeTokenApi, refreshTokenApi, credentials } =
			getConfig()

		const data = new FormData()
		files.forEach((file) => data.append('files', file))

		const opts = {
			...options,
			data,
			params: {
				...(options.params || {}),
				type
			}
		} as Omit<ApiServiceRequestOptionsWithData, 'version'>

		const config = generateConfigs(uploadApi.method, opts)
		let url = createUrl(uploadApi.scope, uploadApi.path, uploadApi.version)

		if (config.params) {
			const params: string[] = []
			Object.entries(config.params).forEach((param) =>
				params.push(param.join('='))
			)

			url = `${url}?${params.join('&')}`
		}

		const xhr = new XMLHttpRequest()
		xhr.open(uploadApi.method, url, true)

		if (config.headers) {
			Object.entries(config.headers).forEach((header) => {
				xhr.setRequestHeader(header[0], header[1] as string)
			})
		}

		xhr.addEventListener('load', async () => {
			let responseData: Nullable<unknown> = null

			try {
				responseData = JSON.parse(xhr.response || '')
			} catch {
				responseData = null
			}

			const response = {
				data: responseData,
				status: xhr.status,
				statusText: xhr.statusText,
				headers: transformXhrResponseHeaders(xhr.getAllResponseHeaders()),
				config
			}

			if (xhr.status >= 200 && xhr.status <= 300) {
				return resolve(
					response as HttpClientResponse<
						unknown,
						ApiServiceResponse<ContentUploadResponse>
					>
				)
			} else {
				if (
					xhr.status === 401 &&
					credentials &&
					refreshTokenApi &&
					revokeTokenApi
				) {
					try {
						await refreshToken()

						return upload(files, type, options)
					} catch (refreshTokenError) {
						return Promise.reject(refreshTokenError)
					}
				}

				return reject(new HttpClientError(url, response, config))
			}
		})

		xhr.addEventListener('error', () => {
			const error = new Error(
				xhr.status === 0 ? 'Network request failed' : 'Unknown error'
			)
			error.name = 'TypeError'

			return reject(new HttpClientError(url, undefined, config, error))
		})

		if (options.onProgress) {
			xhr.upload.onprogress = (event) => {
				if (event.lengthComputable) {
					const progressPercent = (event.loaded / event.total) * 100
					options.onProgress?.(progressPercent)
				}
			}
		}

		xhr.send(data)
	})
}
